// src/screens/finance/FinanceOverviewScreen.jsx
// Visão financeira premium: overview, tendência, comparativo, risco financeiro e insight com IA.
// Padrões: erro real do backend (json.message ou text) + atualiza token via x-new-token.
// Insight Premium: POST /psy/insight/payment?id=ID_USUARIO

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import AppSidebar from "../components/AppSidebar";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/financeOverview.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
  PLAN: "plano",
  PLAN_VALUE: "valorPlano",
  STATUS_ASSINATURA: "statusAssinatura",
  ASSINATURA_ATIVA: "assinaturaAtiva",
  AVISO_ASSINATURA: "avisoAssinatura",
  RECURSOS: "recursos",
};

const MONTHS = [
  "Jan",
  "Fev",
  "Mar",
  "Abr",
  "Mai",
  "Jun",
  "Jul",
  "Ago",
  "Set",
  "Out",
  "Nov",
  "Dez",
];

const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const saveAuthToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function safeNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function moneyBR(value) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(safeNumber(value));
}

function percentBR(value) {
  return `${safeNumber(value).toFixed(1).replace(".", ",")}%`;
}

function normalizeText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function parseJsonStorage(value, fallback = {}) {
  try {
    if (!value) return fallback;

    const parsed = JSON.parse(value);

    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function getStoredRecursos() {
  return parseJsonStorage(readAuth(AUTH_KEYS.RECURSOS), {});
}

function isPremiumByPlan(plano) {
  const value = normalizeText(plano);

  return (
    value === "premium" ||
    value === "pro" ||
    value === "psyassist premium" ||
    value.includes("premium")
  );
}

function isStatusLiberado(statusAssinatura) {
  const status = normalizeText(statusAssinatura || "active");

  return ["active", "trialing", "past_due"].includes(status);
}

function isAssinaturaAtiva(value) {
  if (value === "" || value === null || value === undefined) {
    return true;
  }

  return String(value).trim().toLowerCase() !== "false";
}

function canUseFeature(feature) {
  const recursos = getStoredRecursos();

  if (recursos?.[feature] === true) {
    return true;
  }

  if (feature === "ia" && recursos?.insightsIa === true) {
    return true;
  }

  if (feature === "insightsIa" && recursos?.ia === true) {
    return true;
  }

  return (
    isPremiumByPlan(readAuth(AUTH_KEYS.PLAN)) &&
    isStatusLiberado(readAuth(AUTH_KEYS.STATUS_ASSINATURA)) &&
    isAssinaturaAtiva(readAuth(AUTH_KEYS.ASSINATURA_ATIVA))
  );
}

function getCurrentMonthLabel() {
  return new Intl.DateTimeFormat("pt-BR", {
    month: "long",
    year: "numeric",
  }).format(new Date());
}

function getTimeBR(dateLike) {
  const date = dateLike instanceof Date ? dateLike : new Date(dateLike);

  if (Number.isNaN(date.getTime())) return "--:--";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

async function readErrorMessage(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const json = await response.json();
      return json?.message || JSON.stringify(json);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function normalizeSeries(values = []) {
  return (Array.isArray(values) ? values : [])
    .map((item) => Number(item))
    .filter((item) => Number.isFinite(item));
}

function getRiskInfo(riskValue) {
  const risk = clamp(safeNumber(riskValue), 0, 100);

  if (risk >= 70) {
    return {
      tone: "bad",
      label: "Alto",
      title: "Atenção às despesas",
      description:
        "Suas despesas estão consumindo uma parte alta da receita. Vale revisar custos fixos e gastos recorrentes.",
    };
  }

  if (risk >= 45) {
    return {
      tone: "warn",
      label: "Moderado",
      title: "Acompanhar de perto",
      description:
        "Seu financeiro está em zona de atenção. Pequenos ajustes podem melhorar o saldo do mês.",
    };
  }

  return {
    tone: "good",
    label: "Saudável",
    title: "Finanças equilibradas",
    description:
      "Seu índice está controlado. Continue acompanhando entradas e saídas para manter a previsibilidade.",
  };
}

/** SVG Line Chart */
function LineChartSvg({ values = [], tone = "good" }) {
  const nums = normalizeSeries(values);

  if (!nums.length) {
    return (
      <div className="finov-chart-empty">
        <span>Sem dados suficientes para exibir a tendência.</span>
      </div>
    );
  }

  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const range = Math.max(1e-9, max - min);

  const left = 5;
  const right = 95;
  const top = 5;
  const bottom = 36;

  const points = nums.map((value, index) => {
    const x = left + (index * (right - left)) / Math.max(1, nums.length - 1);
    const y = top + ((max - value) * (bottom - top)) / range;
    return { x, y, value };
  });

  const d = points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(" ");

  const areaD = `${d} L ${points[points.length - 1].x.toFixed(2)} ${bottom} L ${points[0].x.toFixed(2)} ${bottom} Z`;

  return (
    <div className={`finov-chart finov-chart--${tone}`}>
      <svg viewBox="0 0 100 42" className="finov-svg" role="img" aria-label="Tendência financeira">
        <defs>
          <linearGradient id="finovLineGradient" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="currentColor" stopOpacity="0.98" />
            <stop offset="1" stopColor="currentColor" stopOpacity="0.62" />
          </linearGradient>

          <linearGradient id="finovAreaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="currentColor" stopOpacity="0.2" />
            <stop offset="1" stopColor="currentColor" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {[8, 16, 24, 32].map((y) => (
          <line key={y} x1="0" y1={y} x2="100" y2={y} className="finov-gridline" />
        ))}

        <path d={areaD} fill="url(#finovAreaGradient)" />
        <path d={d} fill="none" stroke="url(#finovLineGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />

        {points.map((point, index) => (
          <circle key={`${point.x}-${index}`} cx={point.x} cy={point.y} r="1.35" className="finov-dot" />
        ))}
      </svg>

      <div className="finov-xlabels">
        {nums.slice(0, 12).map((_, index) => (
          <span key={index}>{MONTHS[index % 12]}</span>
        ))}
      </div>
    </div>
  );
}

/** SVG Bar Chart */
function BarChartSvg({ values = [], tone = "good" }) {
  const nums = normalizeSeries(values);

  if (!nums.length) {
    return (
      <div className="finov-chart-empty">
        <span>Sem dados suficientes para exibir o comparativo.</span>
      </div>
    );
  }

  const max = Math.max(...nums, 1e-9);
  const count = Math.min(nums.length, 12);
  const baseY = 36;
  const left = 5;
  const right = 95;
  const width = right - left;
  const gap = 1.2;
  const barWidth = (width - gap * (count - 1)) / count;

  return (
    <div className={`finov-chart finov-chart--${tone}`}>
      <svg viewBox="0 0 100 42" className="finov-svg" role="img" aria-label="Comparativo mensal">
        {[8, 16, 24, 32].map((y) => (
          <line key={y} x1="0" y1={y} x2="100" y2={y} className="finov-gridline" />
        ))}

        {nums.slice(0, count).map((value, index) => {
          const height = Math.max(1.2, (value / max) * 28);
          const x = left + index * (barWidth + gap);
          const y = baseY - height;

          return (
            <rect
              key={index}
              x={x}
              y={y}
              width={barWidth}
              height={height}
              rx="1.4"
              className="finov-bar"
            />
          );
        })}
      </svg>

      <div className="finov-xlabels">
        {nums.slice(0, count).map((_, index) => (
          <span key={index}>{MONTHS[index % 12]}</span>
        ))}
      </div>
    </div>
  );
}

function InsightCard({ text }) {
  return (
    <article className="finov-insight">
      <div className="finov-insight__icon">✨</div>
      <p>{text}</p>
    </article>
  );
}

function OverviewSkeleton() {
  return (
    <div className="finov-skeleton-wrap" aria-hidden="true">
      <div className="finov-skeleton-hero" />
      <div className="finov-skeleton-grid">
        <span />
        <span />
        <span />
      </div>
      <div className="finov-skeleton-panel" />
      <div className="finov-skeleton-panel" />
    </div>
  );
}

export default function FinanceOverviewScreen() {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const [overview, setOverview] = useState(null);
  const [insights, setInsights] = useState([]);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  const isPremium = useMemo(() => canUseFeature("ia") || canUseFeature("insightsIa"), []);

  const authOrRedirect = useCallback(() => {
    const token = readAuth(AUTH_KEYS.TOKEN);
    const userId = readAuth(AUTH_KEYS.USER_ID);

    if (!token || !userId) {
      toast.error("Sessão expirada. Faça login novamente.");
      navigate("/login", { replace: true });
      return null;
    }

    return { token, userId };
  }, [navigate]);

  const fetchInsights = useCallback(
    async (financeData, auth) => {
      if (!financeData || !auth) return;

      setInsightError("");
      setInsightLoading(true);

      try {
        const details = [
          `Receita: ${moneyBR(financeData?.receita)}`,
          `Despesas: ${moneyBR(financeData?.despesa)}`,
          `Saldo: ${moneyBR(financeData?.saldo ?? safeNumber(financeData?.receita) - safeNumber(financeData?.despesa))}`,
          `Tendência: ${(financeData?.tendenciaFinanceira || []).join(", ")}`,
          `Comparativo: ${(financeData?.comparativoMensal || []).join(", ")}`,
          `Risco Financeiro: ${percentBR(financeData?.riscoFinanceiro)}`,
        ].join("\n");

        const response = await fetch(
          `${API_BASE}/psy/insight/payment?id=${encodeURIComponent(auth.userId)}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              userId: auth.userId,
              paymentDetails: details,
            }),
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401) {
          toast.error("Sessão expirada. Faça login novamente.");
          navigate("/login", { replace: true });
          return;
        }

        if (response.status === 403) {
          const message = await readErrorMessage(response);
          setInsightError(message || "Recurso disponível apenas para o plano Premium.");
          setInsights([]);
          return;
        }

        if (!response.ok) {
          const message = await readErrorMessage(response);
          setInsightError(message || "Não foi possível gerar o insight financeiro.");
          setInsights([]);
          return;
        }

        const json = await response.json();
        const text = json?.insight ? String(json.insight).trim() : "";

        setInsights(text ? [text] : []);
      } catch (error) {
        console.error("Erro ao gerar insights:", error);
        setInsightError("Falha de comunicação ao gerar o insight financeiro.");
        setInsights([]);
      } finally {
        setInsightLoading(false);
      }
    },
    [navigate]
  );

  const fetchOverview = useCallback(
    async ({ silent = false } = {}) => {
      const auth = authOrRedirect();
      if (!auth) return;

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await fetch(
          `${API_BASE}/psy/finance/overview?id=${encodeURIComponent(auth.userId)}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${auth.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        saveAuthToken(response.headers.get("x-new-token"));

        if (response.status === 401 || response.status === 403) {
          toast.error("Sessão expirada. Faça login novamente.");
          navigate("/login", { replace: true });
          return;
        }

        if (!response.ok) {
          const message = await readErrorMessage(response);
          toast.error(message);
          setOverview(null);
          setInsights([]);
          setInsightError("");
          return;
        }

        const data = await response.json();
        const safeData = data || null;

        setOverview(safeData);
        setLastUpdatedAt(new Date());

        if (isPremium && safeData) {
          await fetchInsights(safeData, auth);
        } else {
          setInsights([]);
          setInsightError("");
        }
      } catch (error) {
        console.error("Erro ao buscar visão financeira:", error);
        toast.error("Falha de comunicação com a API.");
        setOverview(null);
        setInsights([]);
        setInsightError("");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [authOrRedirect, fetchInsights, isPremium, navigate]
  );

  const handleGenerateInsight = useCallback(async () => {
    const auth = authOrRedirect();

    if (!auth || !overview) return;

    await fetchInsights(overview, auth);
  }, [authOrRedirect, fetchInsights, overview]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  const numbers = useMemo(() => {
    const receita = safeNumber(overview?.receita);
    const despesa = safeNumber(overview?.despesa);
    const saldo = safeNumber(overview?.saldo, receita - despesa);
    const risco = clamp(safeNumber(overview?.riscoFinanceiro), 0, 100);

    const tendencia = normalizeSeries(overview?.tendenciaFinanceira);
    const comparativo = normalizeSeries(overview?.comparativoMensal);

    const margem = receita > 0 ? (saldo / receita) * 100 : 0;
    const despesaRatio = receita > 0 ? (despesa / receita) * 100 : 0;

    return {
      receita,
      despesa,
      saldo,
      risco,
      tendencia,
      comparativo,
      margem,
      despesaRatio,
    };
  }, [overview]);

  const riskInfo = useMemo(() => getRiskInfo(numbers.risco), [numbers.risco]);

  const hasChartData = numbers.tendencia.length || numbers.comparativo.length;

  return (
    <div className="finov-page">
      <div className="finov-bg" aria-hidden="true">
        <span className="finov-bg__orb finov-bg__orb--one" />
        <span className="finov-bg__orb finov-bg__orb--two" />
        <span className="finov-bg__orb finov-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="finov-topbar">
        <button
          className="finov-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          ☰
        </button>

        <div className="finov-brand">
          <img src={Logo} alt="PsyAssist" className="finov-brand__logo" />
          <div className="finov-brand__text">
            <strong>Visão financeira</strong>
            <span>Financeiro • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="finov-top-actions">
          <button
            className="finov-btn finov-btn--ghost finov-btn--desktop"
            type="button"
            onClick={() => navigate("/finance")}
          >
            Voltar
          </button>

          <button
            className="finov-btn finov-btn--primary"
            type="button"
            onClick={() => fetchOverview({ silent: true })}
            disabled={loading || refreshing}
          >
            {refreshing ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </header>

      <main className="finov-main">
        {loading ? (
          <OverviewSkeleton />
        ) : !overview ? (
          <section className="finov-state">
            <div className="finov-state__icon">📊</div>
            <strong>Não foi possível carregar os dados.</strong>
            <span>Verifique sua conexão ou tente atualizar a visão financeira.</span>

            <div className="finov-state__actions">
              <button
                className="finov-btn finov-btn--primary"
                type="button"
                onClick={() => fetchOverview({ silent: true })}
                disabled={refreshing}
              >
                Tentar novamente
              </button>

              <button
                className="finov-btn finov-btn--ghost-dark"
                type="button"
                onClick={() => navigate("/finance")}
              >
                Voltar ao financeiro
              </button>
            </div>
          </section>
        ) : (
          <>
            <section className="finov-hero">
              <div>
                <span className="finov-kicker">Resumo do mês</span>
                <h1>Visão financeira</h1>
                <p>
                  Dados de {getCurrentMonthLabel()}, com análise de receita,
                  despesas, saldo, risco e tendência financeira.
                </p>
              </div>

              <aside className={`finov-hero-card finov-hero-card--${riskInfo.tone}`}>
                <div className="finov-hero-card__icon">≋</div>
                <div>
                  <small>Saldo atual</small>
                  <strong>{moneyBR(numbers.saldo)}</strong>
                  <span>
                    Margem de {percentBR(numbers.margem)} • risco {riskInfo.label.toLowerCase()}.
                  </span>
                </div>
              </aside>
            </section>

            <section className="finov-summary">
              <article className="finov-scard finov-scard--up">
                <div className="finov-scard__icon">↑</div>
                <span>Receita</span>
                <strong>{moneyBR(numbers.receita)}</strong>
                <small>Entradas do mês</small>
              </article>

              <article className="finov-scard finov-scard--down">
                <div className="finov-scard__icon">↓</div>
                <span>Despesas</span>
                <strong>{moneyBR(numbers.despesa)}</strong>
                <small>{percentBR(numbers.despesaRatio)} da receita</small>
              </article>

              <article className="finov-scard finov-scard--wallet">
                <div className="finov-scard__icon">≋</div>
                <span>Saldo</span>
                <strong>{moneyBR(numbers.saldo)}</strong>
                <small>{numbers.saldo >= 0 ? "Resultado positivo" : "Resultado negativo"}</small>
              </article>
            </section>

            <section className="finov-grid">
              <article className="finov-panel finov-panel--chart">
                <div className="finov-panel__head">
                  <div>
                    <span>Tendência</span>
                    <h2>Tendência financeira</h2>
                    <p>Evolução dos valores ao longo dos meses.</p>
                  </div>
                </div>

                <LineChartSvg values={numbers.tendencia} tone={numbers.saldo >= 0 ? "good" : "bad"} />
              </article>

              <article className="finov-panel finov-panel--risk">
                <div className="finov-panel__head">
                  <div>
                    <span>Risco</span>
                    <h2>Risco financeiro</h2>
                    <p>{riskInfo.title}</p>
                  </div>

                  <strong className={`finov-risk-tag finov-risk-tag--${riskInfo.tone}`}>
                    {riskInfo.label}
                  </strong>
                </div>

                <div className="finov-risk">
                  <div className="finov-risk__value">{percentBR(numbers.risco)}</div>

                  <div className="finov-risk__bar">
                    <span
                      className={`finov-risk__fill finov-risk__fill--${riskInfo.tone}`}
                      style={{ width: `${numbers.risco}%` }}
                    />
                  </div>

                  <p>{riskInfo.description}</p>
                </div>
              </article>

              <article className="finov-panel finov-panel--chart">
                <div className="finov-panel__head">
                  <div>
                    <span>Comparativo</span>
                    <h2>Comparativo mensal</h2>
                    <p>Distribuição comparativa por mês.</p>
                  </div>
                </div>

                <BarChartSvg values={numbers.comparativo} tone="good" />
              </article>

              <article className="finov-panel finov-panel--insights">
                <div className="finov-panel__head">
                  <div>
                    <span>Análise com IA</span>
                    <h2>
                      Insight financeiro
                      {isPremium ? <b>Premium</b> : null}
                    </h2>
                    <p>
                      {isPremium
                        ? "Gere recomendações com base em receitas, despesas, saldo e risco."
                        : "Disponível para plano Premium."}
                    </p>
                  </div>

                  {isPremium ? (
                    <button
                      className="finov-ai-btn"
                      type="button"
                      onClick={handleGenerateInsight}
                      disabled={insightLoading || refreshing}
                    >
                      {insightLoading ? "Gerando..." : "Gerar insight"}
                    </button>
                  ) : null}
                </div>

                {insightError ? (
                  <div className="finov-insight-error">{insightError}</div>
                ) : null}

                {!isPremium ? (
                  <div className="finov-premium-lock">
                    <div>🔒</div>
                    <strong>Recurso Premium</strong>
                    <span>Atualize seu plano para gerar insights financeiros com IA.</span>

                    <button
                      className="finov-btn finov-btn--primary"
                      type="button"
                      onClick={() => navigate("/plan-management")}
                    >
                      Ver planos
                    </button>
                  </div>
                ) : insightLoading ? (
                  <div className="finov-insight-loading">
                    <span className="finov-spinner" />
                    Gerando insight financeiro...
                  </div>
                ) : insights.length ? (
                  <div className="finov-insights">
                    {insights.map((text, index) => (
                      <InsightCard text={text} key={`${text}-${index}`} />
                    ))}
                  </div>
                ) : (
                  <div className="finov-insight-ready">
                    <div>✨</div>
                    <strong>Insight pronto para gerar</strong>
                    <span>
                      Clique em “Gerar insight” para analisar o mês atual com IA.
                    </span>
                  </div>
                )}
              </article>
            </section>

            {!hasChartData ? (
              <section className="finov-note">
                <strong>Observação:</strong> cadastre mais receitas e despesas
                para melhorar a qualidade dos gráficos e análises.
              </section>
            ) : null}

            {lastUpdatedAt ? (
              <div className="finov-updated">
                Atualizado às {getTimeBR(lastUpdatedAt)}
              </div>
            ) : null}
          </>
        )}
      </main>
    </div>
  );
}
